import { useLingui } from "@lingui/react/macro";
import type { MessageBlock } from "@rakazo/contracts";
import type { ResponseAttachment } from "@rakazo/core";
import { Check, Circle, LoaderCircle, CircleAlert } from "lucide-react";
import type { ArtifactTarget } from "../lib/artifact-open";
import { ArtifactFileCard } from "./ArtifactFileCard";
import { ArtifactImage } from "../pages/shell/message-cards";

export function TaskPlanCard({ block }: { block: Extract<MessageBlock, { kind: "steps" }> }) {
  const { t } = useLingui();
  const done = block.steps.filter((s) => s.status === "completed").length;
  return (
    <section
      data-testid="task-plan"
      aria-label={t`Task progress`}
      className="rounded-2xl border border-border bg-card p-4 text-base"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="min-w-0 break-words font-medium">{block.summary}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {done}/{block.steps.length}
        </span>
      </div>
      <ol className="space-y-3">
        {block.steps.map((step, index) => {
          const status = step.status ?? "pending";
          const Icon =
            status === "completed"
              ? Check
              : status === "in_progress"
                ? LoaderCircle
                : status === "blocked"
                  ? CircleAlert
                  : Circle;
          return (
            <li
              key={step.id ?? index}
              data-plan-status={status}
              aria-current={status === "in_progress" ? "step" : undefined}
              className="flex items-start gap-3"
            >
              <Icon
                aria-label={
                  status === "completed"
                    ? t`Done`
                    : status === "in_progress"
                      ? t`Working`
                      : status === "blocked"
                        ? t`Blocked`
                        : t`To do`
                }
                className={`mt-0.5 size-5 shrink-0 ${status === "in_progress" ? "animate-spin motion-reduce:animate-none text-foreground" : status === "blocked" ? "text-warning" : "text-muted-foreground"}`}
              />
              <span
                className={`min-w-0 break-words ${status === "completed" ? "text-muted-foreground" : status === "in_progress" ? "font-medium" : "text-foreground/80"}`}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function ResponseAttachments({
  target,
  attachments,
}: {
  target: ArtifactTarget;
  attachments: readonly ResponseAttachment[];
}) {
  if (!attachments.length) return null;
  return (
    <div data-testid="response-attachments" className="space-y-3 pt-2">
      {attachments.map((attachment, index) => (
        <div
          key={`${attachment.messageId}:${index}`}
          data-message-id={attachment.messageId}
          data-attachment-message-id={attachment.messageId}
          className="min-w-0 space-y-2"
        >
          {attachment.blocks
            .filter((b) => b.kind === "file")
            .map((file) =>
              file.kind === "file" ? (
                <div
                  key={file.artifactId}
                  className="overflow-hidden rounded-xl border border-border"
                >
                  <ArtifactFileCard
                    target={target}
                    artifactId={file.artifactId}
                    name={file.name}
                    mimeType={file.mimeType}
                    size={file.size}
                  />
                </div>
              ) : null,
            )}
          {attachment.blocks.some((b) => b.kind === "image") ? (
            <div className="grid grid-cols-4 gap-2">
              {attachment.blocks
                .filter((b) => b.kind === "image")
                .map((image) =>
                  image.kind === "image" ? (
                    <ArtifactImage
                      key={image.artifactId}
                      target={target}
                      artifactId={image.artifactId}
                      name={image.name}
                    />
                  ) : null,
                )}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
